import { z } from "zod";

/**
 * Who the visitor is, in the terms the Clarity dashboard filters by.
 *
 * Product decision of 2026-10-22: the email goes to Clarity in plain text — as the custom user id
 * (normalised, because the dashboard hashes what is typed in its filter), as the friendly name
 * shown on the session card, and as the `email` tag. It is a deliberate exception to the "no PII
 * in analytics" rule, valid for the Clarity identification only; `dataLayer`/GTM stay free of PII
 * (`track` and `panel-analytics.ts` are untouched).
 *
 * **Since 2026-10-28 (D-07):** when the backend has issued the analytics `user_id`, it becomes the
 * custom user id — it ties the recording to GA4 and to the lake, which the email cannot. The email
 * stays searchable, in the `email` tag and in the friendly name, because operations needs it. With
 * no `user_id` (the backend does not serve it yet), the identification is the email, as before.
 *
 * Tag keys are English snake_case and stable: they are what people type into the dashboard.
 */

/** What the dashboard calls the kind of visitor. `host` is a customer who has a list. */
export type ViewerRole = "host" | "customer" | "internal" | "anonymous";

export type ClarityIdentity = {
  /** Normalised email, or `null` for a visitor nobody identified. */
  customId: string | null;
  friendlyName: string | null;
  tags: Record<string, string>;
};

function anonymous(): ClarityIdentity {
  return { customId: null, friendlyName: null, tags: { viewer_role: "anonymous" } };
}

/** Trimmed and lower-cased: the form the dashboard filter will be fed in. */
const emailSchema = z.string().trim().toLowerCase().min(1);

/** An id that is present but empty is as good as absent. */
const optionalId = z.string().trim().min(1).optional().catch(undefined);

const identitySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("customer"),
    email: emailSchema,
    customerId: optionalId,
    giftListId: optionalId,
    analyticsUserId: optionalId,
  }),
  z.object({
    kind: z.literal("internal"),
    email: emailSchema,
    role: optionalId,
  }),
]);

/**
 * Turns the route's answer into the identity. Anything that is not exactly that shape — a failed
 * request, another version of the answer, a visitor — is an anonymous visitor, never an error.
 */
export function parseClarityIdentity(json: unknown): ClarityIdentity {
  const parsed = identitySchema.safeParse(json);
  if (!parsed.success) return anonymous();

  const data = parsed.data;
  const tags: Record<string, string> = { email: data.email };

  if (data.kind === "customer") {
    tags.viewer_role = data.giftListId ? "host" : "customer";
    if (data.customerId) tags.customer_id = data.customerId;
    if (data.giftListId) tags.gift_list_id = data.giftListId;
  } else {
    tags.viewer_role = "internal";
    if (data.role) tags.internal_role = data.role;
  }

  // The email is the identity of whoever has no analytics id; with one, the id leads.
  const customId = data.kind === "customer" && data.analyticsUserId ? data.analyticsUserId : data.email;

  return { customId, friendlyName: data.email, tags };
}

const IDENTITY_ROUTE = "/api/analytics/clarity-identity";

let answer: Promise<unknown> | null = null;

/**
 * Asks the route once per document and remembers the raw answer. Every change of identity — login,
 * signup, "not you?", logout, expired session — ends in a full page load, which starts a new
 * document, so within one document the answer cannot go stale on who the person is.
 *
 * **One request for every reader.** Clarity (`loadClarityIdentity`) and the `dataLayer`'s `user`
 * block (`analytics-user.ts`) both read this same answer; a second consumer must never mean a
 * second request per document.
 *
 * Never rejects: any failure is `null`, which every reader takes as a visitor.
 */
export function loadIdentityAnswer(): Promise<unknown> {
  answer ??= fetchAnswer();

  return answer;
}

async function fetchAnswer(): Promise<unknown> {
  try {
    const response = await fetch(IDENTITY_ROUTE, { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) return null;

    return await response.json();
  } catch {
    return null;
  }
}

/** Never rejects: any failure is an anonymous visitor. */
export async function loadClarityIdentity(): Promise<ClarityIdentity> {
  return parseClarityIdentity(await loadIdentityAnswer());
}
